<template>
	<v-container>
		<div class="py-2">
			<v-breadcrumbs :items="breadcrumbs" class="px-0">
				<template v-slot:divider>
					<v-icon>mdi-chevron-right</v-icon>
				</template>
			</v-breadcrumbs>

			<v-card elevation="0" :loading="loading">
				<v-card-text class="pa-8">
					<div class="ma-4" v-if="loading && !supply">Cargando...</div>

					<v-form
						ref="form"
						v-model="valid"
						lazy-validation
						@submit.prevent="saveSupply"
						v-if="supply"
					>
						<div class="text-subtitle-1 font-weight-medium mb-4">
							Editar abastecimiento
						</div>
						<v-row>
							<v-col cols="12" md="6">
								<v-text-field
									v-model="form.name"
									label="Nombre del contrato"
									outlined
									dense
									:rules="requiredRule"
								/>
							</v-col>
							<v-col cols="12" md="6">
								<v-text-field
									v-model="form.razon_social"
									label="Razón social"
									outlined
									dense
									:rules="requiredRule"
								/>
							</v-col>
							<v-col cols="12" md="6">
								<v-text-field
									v-model="form.start_date"
									type="date"
									label="Fecha de inicio del contrato/adenda"
									outlined
									dense
									:rules="startDateRules"
								/>
							</v-col>
							<v-col cols="12" md="6">
								<v-text-field
									v-model="form.end_date"
									type="date"
									label="Fecha de término del contrato/adenda"
									outlined
									dense
									:rules="endDateRules"
								/>
							</v-col>
							<v-col cols="12">
								<v-autocomplete
									v-model="form.owner"
									:items="owners"
									item-value="id"
									item-text="name"
									placeholder="Búsqueda de cliente/proveedor"
									outlined
									dense
									no-filter
									return-object
									:loading="ownerLoading"
									:rules="requiredRule"
									@update:search-input="searchOwner"
								/>
							</v-col>
						</v-row>

						<div class="d-flex justify-end mt-2">
							<v-btn
								outlined
								class="text-none mr-3"
								:to="{ name: 'ReadAgreement', params: { agreementId: supplyId } }"
							>
								Cancelar
							</v-btn>
							<v-btn
								type="submit"
								color="primary"
								class="text-none"
								depressed
								dark
								:loading="saving"
							>
								Guardar cambios
								<v-icon right light>mdi-content-save</v-icon>
							</v-btn>
						</div>
					</v-form>

					<div v-if="!loading && !supply" class="text-body-1 text--secondary">
						Registro no encontrado.
					</div>
				</v-card-text>
			</v-card>
		</div>
	</v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { showSnack } from '@/helpers/globalHelpers';

export default {
	name: 'EditSupplyView',

	data() {
		return {
			valid: true,
			saving: false,
			ownerLoading: false,
			ownerTimeout: null,
			form: {
				name: null,
				razon_social: null,
				start_date: null,
				end_date: null,
				owner: null,
			},
			requiredRule: [(v) => !!v || 'Campo requerido'],
		};
	},

	computed: {
		...mapState('supply', ['supply', 'loading']),
		...mapState('owner', ['owners']),

		supplyId() {
			return this.$route.params?.id || null;
		},

		breadcrumbs() {
			return [
				{
					text: 'Inicio',
					link: true,
					exact: true,
					disabled: false,
					to: { name: 'Dashboard' },
				},
				{
					text: 'Abastecimiento',
					link: true,
					exact: true,
					disabled: false,
					to: { name: 'AgreementHome' },
				},
				{
					text: this.supply?.name || 'Documento',
					link: true,
					exact: true,
					disabled: false,
					to: { name: 'ReadAgreement', params: { agreementId: this.supplyId } },
				},
				{
					text: 'Editar',
					disabled: true,
					href: '/',
				},
			];
		},

		startDateRules() {
			return [
				(v) => !!v || 'Campo requerido',
				(v) =>
					!this.form.end_date ||
					v < this.form.end_date ||
					'La fecha debe ser menor a la fecha de término',
			];
		},

		endDateRules() {
			return [
				(v) => !!v || 'Campo requerido',
				(v) =>
					!this.form.start_date ||
					v > this.form.start_date ||
					'La fecha debe ser mayor a la fecha de inicio',
			];
		},
	},

	async created() {
		await this.fetchSupply();
	},

	methods: {
		...mapActions('supply', ['getSupply', 'updateSupply']),
		...mapActions('owner', ['listOwners']),

		async fetchSupply() {
			if (!this.supplyId) return;
			const { ok, error } = await this.getSupply(this.supplyId);
			if (!ok) {
				showSnack(error?.data?.message, 'error');
				return;
			}
			this.populateForm();
			await this.preloadOwner();
		},

		populateForm() {
			if (!this.supply) return;
			this.form.name = this.supply.name;
			this.form.razon_social = this.supply.razon_social;
			this.form.start_date = this.supply.start_date?.slice(0, 10) || null;
			this.form.end_date = this.supply.end_date?.slice(0, 10) || null;
			this.form.owner = this.supply.owner || null;
		},

		async preloadOwner() {
			if (!this.supply?.owner?.name) return;
			this.ownerLoading = true;
			await this.listOwners({ q: this.supply.owner.name, type: 'PROVIDER' });
			this.ownerLoading = false;
		},

		searchOwner(value) {
			clearTimeout(this.ownerTimeout);
			if ((value || '').length > 1 || value === '') {
				this.ownerTimeout = setTimeout(async () => {
					this.ownerLoading = true;
					await this.listOwners({ q: value, type: 'PROVIDER' });
					this.ownerLoading = false;
				}, 800);
			}
		},

		async saveSupply() {
			if (!this.$refs.form.validate()) return;

			this.saving = true;
			const { ok, error } = await this.updateSupply({
				id: this.supplyId,
				payload: {
					name: this.form.name,
					razon_social: this.form.razon_social,
					start_date: this.form.start_date,
					end_date: this.form.end_date,
					owner_id: this.form.owner?.id,
				},
			});

			if (ok) {
				showSnack('Abastecimiento actualizado exitosamente', 'success');
				this.$router.push({
					name: 'ReadAgreement',
					params: { agreementId: this.supplyId },
				});
			} else {
				let msg = error?.data?.message;
				msg = Array.isArray(msg) ? msg.join('\n') : msg;
				showSnack(msg || 'Error al actualizar', 'error');
			}

			this.saving = false;
		},
	},

	beforeDestroy() {
		clearTimeout(this.ownerTimeout);
		this.$store.commit('supply/SET_SUPPLY', null);
	},
};
</script>
